import type { CarPhysics, InputState, VectorState } from '../core/types';

/**
 * The racing simulation deliberately depends on these small structural types
 * instead of a renderer or a specific track implementation. Three.js objects,
 * procedural tracks, and unit-test tracks can all be adapted to this contract.
 */
export interface TrackTransform {
  position: VectorState;
  yaw: number;
  pitch?: number;
  roll?: number;
  progress?: number;
  sampleIndex?: number;
}

export interface TrackSurfaceSample {
  progress: number;
  position: VectorState;
  forward: VectorState;
  /** Width of the driveable road, measured edge to edge. */
  width: number;
  right?: VectorState;
  up?: VectorState;
  bank?: number;
  barriers?: 'both' | 'left' | 'right' | 'none';
  sampleIndex?: number;
  /** Set false for intentional gaps and other non-driveable curve sections. */
  driveable?: boolean;
  /** Prevents downward ground adhesion at a ramp lip. */
  launch?: boolean;
}

export interface TrackNearestPoint {
  progress: number;
  sample: TrackSurfaceSample;
  /** Distance along the track. Runtimes may use this for nearest-point lookup. */
  distance: number;
  /** Planar distance from the road center. Preferred when supplied. */
  distanceToCenter?: number;
  lateralOffset?: number;
  onRoad?: boolean;
  verticalOffset?: number;
}

export interface TrackBooster {
  id: string;
  progress: number;
  /** World-space activation radius. Defaults to a road-sized pad. */
  radius?: number;
  /** Alias used by generated track markers. */
  triggerRadius?: number;
  /** Acceleration in metres per second squared while active. */
  acceleration?: number;
  duration?: number;
  /** Optional immediate minimum speed, useful before large jumps. */
  minimumSpeed?: number;
  /** Optional boost speed cap. Defaults to 122% of the vehicle's top speed. */
  maxSpeed?: number;
}

export interface TrackRecoveryRules {
  fallY?: number;
  offTrackGraceSeconds?: number;
  upsideDownGraceSeconds?: number;
  playableMargin?: number;
}

export interface VehicleTrackQuery {
  sampleAt(progress: number): TrackSurfaceSample;
  nearest(position: VectorState, hintProgress?: number): TrackNearestPoint;
  boosters?: readonly TrackBooster[];
  recovery?: TrackRecoveryRules;
  isWithinPlayableArea?(position: VectorState): boolean;
}

export interface VehicleControllerOptions {
  physics: CarPhysics;
  track: VehicleTrackQuery;
  initialTransform: TrackTransform;
  fixedTimestep?: number;
  rideHeight?: number;
}

export interface VehicleStepContext {
  controlsEnabled?: boolean;
  paused?: boolean;
}

export type RecoveryReason = 'fell' | 'left-playable-area' | 'upside-down';

export interface VehicleRecoverySignal {
  reason: RecoveryReason;
  position: VectorState;
  progress: number;
}

export interface VehicleRecoveryStatus {
  onTrack: boolean;
  inPlayableArea: boolean;
  offTrackSeconds: number;
  upsideDownSeconds: number;
  pending: VehicleRecoverySignal | null;
}

export interface VehicleUpdateResult {
  steps: number;
  boosterTriggered: string | null;
  landingImpact: number;
  recovery: VehicleRecoverySignal | null;
}

export interface RaceCheckpoint {
  id: string;
  progress: number;
  resetTransform: TrackTransform;
}

export interface RaceManagerOptions {
  trackId: string;
  carId: string;
  startTransform: TrackTransform;
  checkpoints: readonly RaceCheckpoint[];
  bestTimeMs?: number | null;
  countdownSeconds?: number;
  goDisplaySeconds?: number;
  autoStart?: boolean;
}

export interface RaceStateSnapshot {
  phase: 'loading' | 'countdown' | 'racing' | 'finished';
  timeMs: number;
  countdownText: string | null;
  completedCheckpoints: number;
  checkpointCount: number;
  nextCheckpointId: string | null;
  missingCheckpointWarning: boolean;
  notice: string | null;
  paused: boolean;
  canDrive: boolean;
}

export type CheckpointAttemptReason =
  | 'accepted'
  | 'already-passed'
  | 'out-of-order'
  | 'unknown-checkpoint'
  | 'race-not-active';

export interface CheckpointAttempt {
  accepted: boolean;
  reason: CheckpointAttemptReason;
  completedCheckpoints: number;
  expectedCheckpointId: string | null;
}

export interface FinishAttempt {
  accepted: boolean;
  missingCheckpoints: number;
  result: import('../core/types').RaceResult | null;
}

export const NEUTRAL_INPUT: Readonly<InputState> = Object.freeze({
  throttle: 0,
  brake: 0,
  steer: 0,
  handbrake: false,
  restartPressed: false,
  resetPressed: false,
  pausePressed: false,
});
